import { useCallback, useState } from 'react';
import { supabase } from '../lib/supabase';

export type DocumentType = 'pilot-assessment' | 'instructor-assessment' | 'debriefing-guide';
export type DocumentFormat = 'pdf' | 'docx';

interface UseDocumentAPIOptions {
  onExportStart?: () => void;
  onExportSuccess?: (blob: Blob, filename: string) => void;
  onExportError?: (error: Error) => void;
}

interface UseDocumentAPIReturn {
  generateDocument: (
    documentType: DocumentType,
    format: DocumentFormat,
    data: Record<string, unknown>,
    defaultFilename: string,
  ) => Promise<Blob>;
  isExporting: boolean;
  error: Error | null;
}

export function useDocumentAPI({
  onExportStart,
  onExportSuccess,
  onExportError,
}: UseDocumentAPIOptions = {}): UseDocumentAPIReturn {
  const [isExporting, setIsExporting] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const generateDocument = useCallback(
    async (
      documentType: DocumentType,
      format: DocumentFormat,
      data: Record<string, unknown>,
      defaultFilename: string,
    ): Promise<Blob> => {
      try {
        setIsExporting(true);
        setError(null);
        onExportStart?.();

        const { data: { session } } = await supabase.auth.getSession();
        if (!session?.access_token) {
          throw new Error('Authentication required. Please sign in again.');
        }

        const response = await fetch('/api/generate-document', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${session.access_token}`,
          },
          body: JSON.stringify({
            documentType,
            format,
            data,
            options: { filename: defaultFilename.replace(/\.(pdf|docx)$/, '') },
          }),
        });

        if (!response.ok) {
          const errorBody = await response.json().catch(() => ({})) as { message?: string; error?: string };
          throw new Error(errorBody.message || errorBody.error || `HTTP ${response.status}`);
        }

        const blob = await response.blob();

        const disposition = response.headers.get('Content-Disposition') ?? '';
        const match = disposition.match(/filename="([^"]+)"/);
        const filename = match?.[1] ?? defaultFilename;

        onExportSuccess?.(blob, filename);
        setIsExporting(false);
        return blob;
      } catch (err) {
        setIsExporting(false);
        const exportError = err instanceof Error ? err : new Error('Document generation failed');
        setError(exportError);
        onExportError?.(exportError);
        throw exportError;
      }
    },
    [onExportStart, onExportSuccess, onExportError],
  );

  return { generateDocument, isExporting, error };
}
